import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Shield, ChevronDown, ChevronUp, ExternalLink as ExternalLinkIcon, X } from "lucide-react";
import ExternalLink from "./ExternalLink";
import { toast } from "sonner";
import { api } from "../lib/api";

const SEVERITY_STYLE = {
  calm:  { bg: "bg-brand-primary/10",  bd: "border-brand-primary/30",  text: "text-brand-primary",  label: "All clear" },
  info:  { bg: "bg-[#D19C4C]/15",      bd: "border-[#D19C4C]/40",      text: "text-[#8a6526]",      label: "Heads-up" },
  warn:  { bg: "bg-brand-accent/15",   bd: "border-brand-accent/40",   text: "text-brand-accent",   label: "Caution" },
  avoid: { bg: "bg-brand-error/15",    bd: "border-brand-error/40",    text: "text-brand-error",    label: "Avoid today" },
};

const TYPES = [
  { key: "sheep", label: "Sheep", icon: "🐑" },
  { key: "cattle", label: "Cattle", icon: "🐄" },
  { key: "horses", label: "Horses", icon: "🐎" },
  { key: "deer", label: "Deer", icon: "🦌" },
  { key: "ground_nesting_birds", label: "Ground-nesting birds", icon: "🪺" },
  { key: "other", label: "Other", icon: "❓" },
];
const RECENCY = [
  { key: "just_now", label: "Just now" },
  { key: "today", label: "Today" },
  { key: "this_week", label: "This week" },
];

export default function LivestockSection({ locationId }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [type, setType] = useState("sheep");
  const [recency, setRecency] = useState("today");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const adv = useQuery({
    queryKey: ["advisory", locationId],
    queryFn: async () => (await api.get(`/locations/${locationId}/livestock-advisory`)).data,
  });

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/locations/${locationId}/livestock-report`, { livestock_type: type, recency, notes });
      toast.success("Thanks — your report helps everyone walk safer.");
      setShowReport(false);
      setNotes("");
      qc.invalidateQueries({ queryKey: ["advisory", locationId] });
    } catch {
      toast.error("Couldn't submit — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (adv.isLoading) {
    return <div className="td-card p-5 mt-5 text-brand-subtle text-sm">Checking countryside conditions…</div>;
  }
  if (!adv.data) return null;
  const s = SEVERITY_STYLE[adv.data.severity] || SEVERITY_STYLE.calm;

  return (
    <section className={`mt-5 rounded-3xl border ${s.bd} ${s.bg} p-5 sm:p-6`} data-testid="livestock-section">
      <div className="flex items-start gap-3">
        <Shield size={20} className={s.text} />
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-xs font-bold uppercase tracking-wider ${s.text}`}>Countryside Watch · {s.label}</div>
              <h3 className="font-heading text-lg font-medium text-brand-text mt-1">{adv.data.month}</h3>
            </div>
            <button
              data-testid="advisory-expand-btn"
              onClick={() => setOpen((x) => !x)}
              className="text-brand-subtle"
              aria-label="Expand"
            >
              {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </button>
          </div>
          <p className="text-sm text-brand-text mt-2 leading-relaxed">{adv.data.summary}</p>

          {open && (
            <div className="mt-4 space-y-3 animate-fade-up">
              {adv.data.seasonal?.length > 0 && (
                <div>
                  <div className="td-section-label mb-2">Seasonal factors</div>
                  <div className="flex flex-wrap gap-1.5">
                    {adv.data.seasonal.map((it) => (
                      <span key={it.name} className="td-chip">{it.icon} {it.name}</span>
                    ))}
                  </div>
                </div>
              )}
              {Object.keys(adv.data.counts || {}).length > 0 && (
                <div>
                  <div className="td-section-label mb-2">Recent walker reports (60d)</div>
                  <div className="flex flex-wrap gap-1.5">
                    {Object.entries(adv.data.counts).map(([k, v]) => (
                      <span key={k} className="td-chip capitalize">{k.replace(/_/g, " ")} × {v}</span>
                    ))}
                  </div>
                </div>
              )}
              <ExternalLink
                href={adv.data.countryside_code_url}
                className="inline-flex items-center gap-1.5 text-sm text-brand-accent font-medium"
              >
                Read the Countryside Code <ExternalLinkIcon size={13} />
              </ExternalLink>
            </div>
          )}

          <button
            data-testid="report-livestock-btn"
            onClick={() => setShowReport(true)}
            className="td-btn-secondary mt-4 !py-2 !text-sm"
          >
            I saw livestock here
          </button>
        </div>
      </div>

      {showReport && (
        <div className="fixed inset-0 z-[1100] bg-brand-text/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-brand-surface w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl p-6 animate-fade-up" data-testid="report-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-heading text-xl">Report livestock</h3>
              <button onClick={() => setShowReport(false)} className="text-brand-subtle" aria-label="Close">
                <X size={20} />
              </button>
            </div>
            <div className="mt-4">
              <div className="td-section-label">Type</div>
              <div className="flex flex-wrap gap-2 mt-2">
                {TYPES.map((t) => (
                  <button
                    key={t.key}
                    data-testid={`livestock-type-${t.key}`}
                    onClick={() => setType(t.key)}
                    className={`td-chip ${type === t.key ? "td-chip-active" : ""}`}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="td-section-label">When</div>
              <div className="flex gap-2 mt-2">
                {RECENCY.map((r) => (
                  <button
                    key={r.key}
                    data-testid={`livestock-recency-${r.key}`}
                    onClick={() => setRecency(r.key)}
                    className={`td-chip ${recency === r.key ? "td-chip-active" : ""}`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4">
              <div className="td-section-label">Notes (optional)</div>
              <textarea
                data-testid="livestock-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="td-input mt-2 resize-none"
                placeholder="Where on the path? Loose or fenced?"
              />
            </div>
            <button
              data-testid="livestock-submit"
              disabled={submitting}
              onClick={submit}
              className="td-btn-primary w-full mt-5"
            >
              {submitting ? "Submitting…" : "Submit report"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
